import { JACK_DEFAULT_PARAMS } from './jackPackingEngine';
import INTERNAL_DEFAULTS from '../config/jackInternalDefaults.json';
import { auth } from '../firebase';

/** Current Firebase user id for namespacing Jack localStorage (null if signed out). */
export function getJackStorageUid() {
  return auth.currentUser?.uid ?? null;
}

/** Per-user localStorage key so bike profiles / linkage presets never leak across accounts on one browser. */
function scopedJackKey(baseKey) {
  const uid = getJackStorageUid();
  if (!uid) return null;
  return `${baseKey}__u_${uid}`;
}

/**
 * One-time: if this user has no scoped value but an old global key exists, move global → scoped and remove global.
 * The first signed-in user on this browser inherits legacy unscoped data; later users get their own empty scoped keys.
 */
function adoptUnscopedJackKeyIntoUser(baseKey) {
  const scoped = scopedJackKey(baseKey);
  if (!scoped) return;
  if (localStorage.getItem(scoped) != null) return;
  const legacy = localStorage.getItem(baseKey);
  if (legacy == null) return;
  try {
    localStorage.setItem(scoped, legacy);
    localStorage.removeItem(baseKey);
  } catch (_e) {
    /* ignore quota */
  }
}

/** @deprecated Legacy single-blob key — migrated once to split keys below */
export const JACK_SETTINGS_KEY = 'suspensionAnalyzerSettings';

export const JACK_MAIN_SETTINGS_KEY = 'jackMainSuspensionSettings_v1';
export const JACK_ADVANCED_OVERRIDES_KEY = 'jackAdvancedOverrides_v1';
export const JACK_ONBOARDING_KEY = 'jackOnboardingCompleted_v1';

/** Saved bike profiles: front/rear travel + rear linkage (a–d). */
export const JACK_BIKE_PROFILES_KEY = 'jackBikeProfiles_v1';

export function newJackBikeProfileId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `p-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function loadBikeProfilesStore() {
  try {
    adoptUnscopedJackKeyIntoUser(JACK_BIKE_PROFILES_KEY);
    const key = scopedJackKey(JACK_BIKE_PROFILES_KEY);
    if (!key) return { profiles: [] };
    const raw = localStorage.getItem(key);
    if (!raw) return { profiles: [] };
    const p = JSON.parse(raw);
    return {
      profiles: Array.isArray(p.profiles) ? p.profiles : [],
    };
  } catch (_e) {
    return { profiles: [] };
  }
}

export function saveBikeProfilesStore(store) {
  const key = scopedJackKey(JACK_BIKE_PROFILES_KEY);
  if (!key) return;
  localStorage.setItem(
    key,
    JSON.stringify({
      profiles: Array.isArray(store.profiles) ? store.profiles : [],
    }),
  );
}

/** User-defined rear linkage presets (name + a–d), editable list — no built-in file. */
export const JACK_USER_LINKAGE_PRESETS_KEY = 'jackUserLinkagePresets_v1';

export function loadUserLinkagePresets() {
  try {
    adoptUnscopedJackKeyIntoUser(JACK_USER_LINKAGE_PRESETS_KEY);
    const key = scopedJackKey(JACK_USER_LINKAGE_PRESETS_KEY);
    if (!key) return [];
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch (_e) {
    return [];
  }
}

export function saveUserLinkagePresets(presets) {
  const key = scopedJackKey(JACK_USER_LINKAGE_PRESETS_KEY);
  if (!key) return;
  localStorage.setItem(key, JSON.stringify(Array.isArray(presets) ? presets : []));
}

/** Fields editable in Advanced Settings (also persisted in jackAdvancedOverrides_v1) */
export const JACK_ADVANCED_UI_FIELDS = [
  'invertFront',
  'invertRear',
  'medianFilterSize',
  'maxPosChange',
  'velocityWindow',
  'filterType',
  'positionFilterWindow',
  'velocityFilterWindow',
  'showRawData',
  'autoReprocess',
  'minStrokeDepth',
  'minStrokeSamples',
  'bottomOutThreshold',
  'topOutThreshold',
  'extensionThreshold',
  'packedThreshold',
  'minPackedHits',
  'stuckThreshold',
  'ratchetDelta',
  'velocityThreshold',
  'tuneReboundP95Min',
  'tuneReboundP95Max',
  'tuneCompressionP95Min',
  'tuneCompressionP95Max',
  'tuneDynamicSagPctMin',
  'tuneDynamicSagPctMax',
  'tuneBalanceRatioMin',
  'tuneBalanceRatioMax',
  'tuneMaxBottomOutsTotal',
  'tuneMaxBottomOutsPerEnd',
  'tunePackingIndexGoodMax',
  'tuneMinTravelUsePct',
  'tuneRoughnessWarnAbove',
  'tuneOpenaiApiKey',
];

/** Shown in Settings but not passed to the Jack packing engine (UI / tuning guide only). */
export const JACK_TUNING_UI_STRIP_KEYS = [
  'tuneReboundP95Min',
  'tuneReboundP95Max',
  'tuneCompressionP95Min',
  'tuneCompressionP95Max',
  'tuneDynamicSagPctMin',
  'tuneDynamicSagPctMax',
  'tuneBalanceRatioMin',
  'tuneBalanceRatioMax',
  'tuneMaxBottomOutsTotal',
  'tuneMaxBottomOutsPerEnd',
  'tunePackingIndexGoodMax',
  'tuneMinTravelUsePct',
  'tuneRoughnessWarnAbove',
  'tuneOpenaiApiKey',
];

export const JACK_ADVANCED_STRING_FIELDS = new Set(['tuneOpenaiApiKey']);

/** Hidden technical params — defaults from jackInternalDefaults.json only (not in main localStorage) */
export const JACK_INTERNAL_ONLY_FIELDS = [
  'sagPercent',
  'frontPotStroke',
  'rearPotStroke',
];

const LEGACY_FIELDS = [
  ...JACK_INTERNAL_ONLY_FIELDS,
  ...JACK_ADVANCED_UI_FIELDS,
  'frontTravel',
  'rearTravel',
  'rearA',
  'rearB',
  'rearC',
  'rearD',
  'trimStart',
  'trimEnd',
];

const DEFAULT_MAIN = {
  /** When no bike profile is selected, travel defaults to zero until you pick or save a profile. */
  frontTravel: 0,
  rearTravel: 0,
  /** saved:id | community:docId | custom */
  linkageSource: 'custom',
  /** Which saved bike profile is active (local profiles list), or null */
  selectedBikeProfileId: null,
  rearA: 0,
  rearB: 2.8574,
  rearC: 0,
  rearD: 0,
};

const ADVANCED_NUMBER_FIELDS = new Set(
  JACK_ADVANCED_UI_FIELDS.filter(
    (f) =>
      f !== 'filterType' &&
      f !== 'showRawData' &&
      f !== 'autoReprocess' &&
      !JACK_ADVANCED_STRING_FIELDS.has(f),
  ),
);
const ADVANCED_BOOLEAN_FIELDS = new Set(['showRawData', 'autoReprocess', 'invertFront', 'invertRear']);

function toNumberOrDefault(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getInternalBase() {
  return { ...JACK_DEFAULT_PARAMS, ...INTERNAL_DEFAULTS };
}

function migrateLegacyIfNeeded() {
  try {
    const uid = getJackStorageUid();
    if (!uid) return;

    const mainScoped = scopedJackKey(JACK_MAIN_SETTINGS_KEY);
    if (!mainScoped) return;
    if (localStorage.getItem(mainScoped)) return;

    const raw = localStorage.getItem(JACK_SETTINGS_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return;

    const main = {
      ...DEFAULT_MAIN,
      frontTravel: toNumberOrDefault(parsed.frontTravel, 0),
      rearTravel: toNumberOrDefault(parsed.rearTravel, 0),
      rearA: Number.isFinite(Number(parsed.rearA)) ? Number(parsed.rearA) : DEFAULT_MAIN.rearA,
      rearB: Number.isFinite(Number(parsed.rearB)) ? Number(parsed.rearB) : DEFAULT_MAIN.rearB,
      rearC: Number.isFinite(Number(parsed.rearC)) ? Number(parsed.rearC) : DEFAULT_MAIN.rearC,
      rearD: Number.isFinite(Number(parsed.rearD)) ? Number(parsed.rearD) : DEFAULT_MAIN.rearD,
      linkageSource: 'custom',
      selectedBikeProfileId: null,
    };
    localStorage.setItem(mainScoped, JSON.stringify(main));

    const adv = {};
    JACK_ADVANCED_UI_FIELDS.forEach((field) => {
      if (parsed[field] === undefined) return;
      if (ADVANCED_BOOLEAN_FIELDS.has(field)) {
        adv[field] = Boolean(parsed[field]);
      } else if (JACK_ADVANCED_STRING_FIELDS.has(field)) {
        adv[field] = String(parsed[field]);
      } else if (ADVANCED_NUMBER_FIELDS.has(field)) {
        adv[field] = toNumberOrDefault(parsed[field], getInternalBase()[field]);
      } else {
        adv[field] = parsed[field];
      }
    });
    const advScoped = scopedJackKey(JACK_ADVANCED_OVERRIDES_KEY);
    if (advScoped && Object.keys(adv).length > 0) {
      localStorage.setItem(advScoped, JSON.stringify(adv));
    }
    const onboardScoped = scopedJackKey(JACK_ONBOARDING_KEY);
    if (onboardScoped) {
      try {
        localStorage.setItem(onboardScoped, '1');
      } catch (_e2) {
        /* ignore */
      }
    }
    try {
      localStorage.removeItem(JACK_SETTINGS_KEY);
    } catch (_r) {
      /* ignore */
    }
  } catch (_e) {
    /* ignore */
  }
}

function migrateMainShape(merged) {
  const m = { ...merged };
  if (m.bikePresetId != null && typeof m.bikePresetId === 'string') {
    const b = m.bikePresetId;
    if (b.startsWith('profile:')) {
      m.selectedBikeProfileId = b.slice('profile:'.length);
      m.linkageSource = 'custom';
    } else {
      m.linkageSource = b;
    }
    delete m.bikePresetId;
  }
  if (m.linkageSource == null || m.linkageSource === '') {
    m.linkageSource = 'custom';
  }
  if (typeof m.linkageSource === 'string' && m.linkageSource.startsWith('builtin:')) {
    m.linkageSource = 'custom';
  }
  if (m.selectedBikeProfileId === undefined) m.selectedBikeProfileId = null;
  return m;
}

export function loadMainJackSettings() {
  migrateLegacyIfNeeded();
  adoptUnscopedJackKeyIntoUser(JACK_MAIN_SETTINGS_KEY);
  try {
    const key = scopedJackKey(JACK_MAIN_SETTINGS_KEY);
    if (!key) return { ...DEFAULT_MAIN };
    const raw = localStorage.getItem(key);
    if (!raw) return { ...DEFAULT_MAIN };
    const parsed = JSON.parse(raw);
    return migrateMainShape({ ...DEFAULT_MAIN, ...parsed });
  } catch (_e) {
    return { ...DEFAULT_MAIN };
  }
}

export function saveMainJackSettings(settings) {
  const key = scopedJackKey(JACK_MAIN_SETTINGS_KEY);
  if (!key) return;
  const payload = {
    frontTravel: settings.frontTravel,
    rearTravel: settings.rearTravel,
    linkageSource: settings.linkageSource,
    selectedBikeProfileId:
      settings.selectedBikeProfileId === '' || settings.selectedBikeProfileId === undefined
        ? null
        : settings.selectedBikeProfileId,
    rearA: settings.rearA,
    rearB: settings.rearB,
    rearC: settings.rearC,
    rearD: settings.rearD,
  };
  localStorage.setItem(key, JSON.stringify(payload));
}

export function loadAdvancedJackOverrides() {
  migrateLegacyIfNeeded();
  adoptUnscopedJackKeyIntoUser(JACK_ADVANCED_OVERRIDES_KEY);
  try {
    const key = scopedJackKey(JACK_ADVANCED_OVERRIDES_KEY);
    if (!key) return {};
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_e) {
    return {};
  }
}

export function saveAdvancedJackOverrides(partial) {
  const key = scopedJackKey(JACK_ADVANCED_OVERRIDES_KEY);
  if (!key) return;
  const prev = loadAdvancedJackOverrides();
  const next = { ...prev, ...partial };
  localStorage.setItem(key, JSON.stringify(next));
}

export function isJackOnboardingComplete() {
  try {
    adoptUnscopedJackKeyIntoUser(JACK_ONBOARDING_KEY);
    const key = scopedJackKey(JACK_ONBOARDING_KEY);
    if (!key) return false;
    return localStorage.getItem(key) === '1';
  } catch (_e) {
    return false;
  }
}

export function setJackOnboardingComplete() {
  try {
    const key = scopedJackKey(JACK_ONBOARDING_KEY);
    if (!key) return;
    localStorage.setItem(key, '1');
  } catch (_e) {
    /* ignore */
  }
}

/** New signup on a browser that already completed onboarding for another user must see bike setup again. */
export function clearJackOnboardingFlag() {
  try {
    const key = scopedJackKey(JACK_ONBOARDING_KEY);
    if (key) localStorage.removeItem(key);
    localStorage.removeItem(JACK_ONBOARDING_KEY);
  } catch (_e) {
    /* ignore */
  }
}

/** Bike profile names and linkage preset names are stored lowercase (trimmed). */
export function normalizeJackBikeLabel(s) {
  return String(s || '').trim().toLowerCase();
}

function nCoeffJackBike(x) {
  const v = Number(x);
  if (!Number.isFinite(v)) return 0;
  return Math.round(v * 1e6) / 1e6;
}

/** Persisted bike profile fields used for dirty detection and saving profiles. */
export function bikeProfileDataSnapshot(mainState) {
  const src =
    typeof mainState.linkageSource === 'string' && mainState.linkageSource.length > 0
      ? mainState.linkageSource
      : 'custom';
  return {
    frontTravel: Math.round(Number(mainState.frontTravel) || 0),
    rearTravel: Math.round(Number(mainState.rearTravel) || 0),
    rearA: nCoeffJackBike(mainState.rearA),
    rearB: nCoeffJackBike(mainState.rearB),
    rearC: nCoeffJackBike(mainState.rearC),
    rearD: nCoeffJackBike(mainState.rearD),
    linkageSource: src,
  };
}

export function bikeProfileDataSnapshotFromProfile(p) {
  return bikeProfileDataSnapshot({
    frontTravel: p.frontTravel,
    rearTravel: p.rearTravel,
    rearA: p.rearA,
    rearB: p.rearB,
    rearC: p.rearC,
    rearD: p.rearD,
    linkageSource: p.linkageSource,
  });
}

export function bikeProfileSnapshotsEqual(a, b) {
  return (
    a.frontTravel === b.frontTravel &&
    a.rearTravel === b.rearTravel &&
    a.rearA === b.rearA &&
    a.rearB === b.rearB &&
    a.rearC === b.rearC &&
    a.rearD === b.rearD &&
    a.linkageSource === b.linkageSource
  );
}

/**
 * Write the active bike profile slice from main state to local storage (explicit save only).
 * @param {{ name?: string }} [options] If `options.name` is a non-empty string after normalization, updates the profile name.
 */
export function persistActiveJackBikeProfile(mainState, options = {}) {
  const pid = mainState.selectedBikeProfileId;
  if (typeof pid !== 'string' || !pid) return;
  const store = loadBikeProfilesStore();
  const idx = store.profiles.findIndex((p) => p.id === pid);
  if (idx < 0) return;
  const snap = bikeProfileDataSnapshot(mainState);
  const updated = [...store.profiles];
  const rawName = options && typeof options.name === 'string' ? options.name : '';
  const nameNorm = normalizeJackBikeLabel(rawName);
  const namePatch = nameNorm ? { name: nameNorm } : {};
  updated[idx] = {
    ...updated[idx],
    ...snap,
    ...namePatch,
  };
  saveBikeProfilesStore({ profiles: updated });
}

/**
 * Time bounds from telemetry `time` column (same space as Jack trim).
 */
export function getTelemetryTimeBounds(telemetryData) {
  if (!Array.isArray(telemetryData) || telemetryData.length === 0) {
    return { minT: 0, maxT: 0 };
  }
  let minT = Infinity;
  let maxT = -Infinity;
  for (let i = 0; i < telemetryData.length; i += 1) {
    const t = telemetryData[i]?.time;
    if (typeof t === 'number' && Number.isFinite(t)) {
      minT = Math.min(minT, t);
      maxT = Math.max(maxT, t);
    }
  }
  if (!Number.isFinite(minT)) return { minT: 0, maxT: 0 };
  return { minT, maxT };
}

/**
 * Per-run trim stored on run as analysisTrim: { startTime?, endTime? } (absolute data times).
 * Omit or null = use full telemetry span (non-destructive).
 */
export function buildTrimOverrideForRide(telemetryData, analysisTrim) {
  const { minT, maxT } = getTelemetryTimeBounds(telemetryData);
  const start =
    analysisTrim && typeof analysisTrim.startTime === 'number' && Number.isFinite(analysisTrim.startTime)
      ? analysisTrim.startTime
      : minT;
  const end =
    analysisTrim && typeof analysisTrim.endTime === 'number' && Number.isFinite(analysisTrim.endTime)
      ? analysisTrim.endTime
      : maxT;
  return { trimStart: start, trimEnd: end };
}

export function getInternalDefaultsForEditing() {
  return { ...getInternalBase() };
}

/**
 * Merge order: engine defaults → internal JSON → advanced overrides → main UI (travel + linkage)
 * → ride-specific jack params / trim overrides.
 */
export function getJackParamsForAnalysis(rideOverride = {}) {
  const internal = getInternalBase();
  const advanced = loadAdvancedJackOverrides();
  const main = loadMainJackSettings();

  const mainParams = {
    frontTravel: main.frontTravel,
    rearTravel: main.rearTravel,
    rearA: main.rearA,
    rearB: main.rearB,
    rearC: main.rearC,
    rearD: main.rearD,
  };

  let merged = {
    ...internal,
    ...advanced,
    ...mainParams,
  };

  if (rideOverride && typeof rideOverride === 'object') {
    merged = { ...merged, ...rideOverride };
  }

  const trimEndRaw = merged.trimEnd;
  const trimEnd =
    trimEndRaw === '' || trimEndRaw === null || trimEndRaw === undefined
      ? Infinity
      : toNumberOrDefault(trimEndRaw, Infinity);

  const forEngine = { ...merged, trimEnd };
  JACK_TUNING_UI_STRIP_KEYS.forEach((k) => {
    delete forEngine[k];
  });
  return forEngine;
}

/**
 * Desirable bands for summary icons + KB copy (advanced overrides merged over internal defaults).
 * Does not include secrets beyond what is already in browser storage.
 */
export function getTuningGuideConfig() {
  const internal = getInternalBase();
  const overrides = loadAdvancedJackOverrides();
  const out = {};
  JACK_TUNING_UI_STRIP_KEYS.forEach((k) => {
    if (JACK_ADVANCED_STRING_FIELDS.has(k)) {
      out[k] = overrides[k] !== undefined ? String(overrides[k]) : '';
    } else {
      const def = internal[k];
      out[k] = overrides[k] !== undefined ? toNumberOrDefault(overrides[k], def) : def;
    }
  });
  return out;
}

/** @deprecated Use loadMainJackSettings / loadAdvancedJackOverrides */
export function loadJackSettings() {
  const internal = getInternalBase();
  const advanced = loadAdvancedJackOverrides();
  const main = loadMainJackSettings();
  const bikePresetId =
    main.selectedBikeProfileId != null
      ? `profile:${main.selectedBikeProfileId}`
      : main.linkageSource;
  return {
    ...internal,
    ...advanced,
    ...main,
    bikePresetId,
    trimStart: 0,
    trimEnd: '',
  };
}

/** @deprecated */
export function saveJackSettings(settings) {
  saveMainJackSettings(settings);
  const adv = {};
  JACK_ADVANCED_UI_FIELDS.forEach((k) => {
    if (settings[k] !== undefined) adv[k] = settings[k];
  });
  saveAdvancedJackOverrides(adv);
}

export const JACK_SETTINGS_FIELDS = LEGACY_FIELDS;
